import React, { useState } from 'react';
import { CreateUserData } from '../../../types';
import icons from '../../../layout/Icons';

interface CreateUserModalProps {
  onClose: () => void;
  onCreate: (userData: CreateUserData) => void;
  error?: string;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreate, error }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const validateField = (name: string, value: string) => {
    const errors: {[key: string]: string} = {};

    switch (name) {
      case 'username':
        if (!value.trim()) {
          errors.username = 'El nombre de usuario es requerido';
        } else if (value.includes(' ')) {
          errors.username = 'El nombre de usuario no puede contener espacios';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
          errors.username = 'Solo se permiten letras, números, guiones y guiones bajos';
        }
        break;
      case 'email':
        if (!value.trim()) {
          errors.email = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Formato de email inválido';
        }
        break;
      case 'first_name':
        if (!value.trim()) {
          errors.first_name = 'El nombre es requerido';
        }
        break;
      case 'last_name':
        if (!value.trim()) {
          errors.last_name = 'El apellido es requerido';
        }
        break;
      case 'password':
        if (!value.trim()) {
          errors.password = 'La contraseña es requerida';
        } else if (value.length < 6) {
          errors.password = 'La contraseña debe tener al menos 6 caracteres';
        }
        break;
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar todos los campos
    let allErrors: {[key: string]: string} = {};
    Object.keys(formData).forEach(key => {
      const fieldErrors = validateField(key, formData[key as keyof CreateUserData]);
      allErrors = { ...allErrors, ...fieldErrors };
    });

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return;
    }

    setLoading(true);
    setValidationErrors({});
    try {
      await onCreate(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Validar el campo en tiempo real
    const fieldErrors = validateField(name, value);
    setValidationErrors(prev => {
      let newErrors = { ...prev, ...fieldErrors };
      // Si el campo es válido, eliminar el error de ese campo
      if (Object.keys(fieldErrors).length === 0) {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Crear Nuevo Usuario</h2>
          <button className="close-btn" onClick={onClose} disabled={loading}>×</button>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="username">Nombre de Usuario *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Ej: jdoe"
              className={validationErrors.username ? 'error' : ''}
            />
            {validationErrors.username && (
              <span className="field-error" style={{ color: 'red', fontWeight: 'bold' }}>{validationErrors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Ej: juan@ejemplo.com"
              className={validationErrors.email ? 'error' : ''}
            />
            {validationErrors.email && (
              <span className="field-error" style={{ color: 'red', fontWeight: 'bold' }}>{validationErrors.email}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">Nombre *</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Juan"
                className={validationErrors.first_name ? 'error' : ''}
              />
              {validationErrors.first_name && (
                <span className="field-error" style={{ color: 'red', fontWeight: 'bold' }}>{validationErrors.first_name}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="last_name">Apellido *</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Pérez"
                className={validationErrors.last_name ? 'error' : ''}
              />
              {validationErrors.last_name && (
                <span className="field-error" style={{ color: 'red', fontWeight: 'bold' }}>{validationErrors.last_name}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Rol *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="employee">Empleado</option>
              <option value="admin">Administrador</option>
            </select>
            <small className="role-note">
              Nota: Los superusers solo pueden ser creados por los administradores del sistema por razones de seguridad
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña *</label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Mínimo 6 caracteres"
                className={validationErrors.password ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? icons.Eye : icons.EyeClose}
              </button>
            </div>
            {validationErrors.password && (
              <span className="field-error" style={{ color: 'red', fontWeight: 'bold' }}>{validationErrors.password}</span>
            )}
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;